import { useNavigate } from "react-router-dom";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa";
import React, { useCallback, useEffect, useRef, useState } from "react";
import RecomendedItem from "../Components/RecomendedItem";
import RecomItems from "../Components/RecomItems";
import { _get, _post } from "../services/apiClient";
import { useLocation } from "react-router-dom";
import GuestDetailsModal from "../Components/GuestDetailsModal";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useCategoryAnimation } from "../hooks/useCategoryAnimation";
import ChefPicks from "../Components/ChefPicks";
import { CategorySkeleton, BannerSkeleton } from "../Components/SkeletonLoader";
import { useRipple } from "../hooks/useRipple";
import { getAppConfig } from "../utilis/appConfig";
import MenuItem from "../Components/MenuItem";
import { useRestaurantDetails } from "../context/RestaurantDetailsContext";
import ServiceUnavailable from "../Components/ServiceUnavailable";
import CoverSplash from "../Components/HomeDecorations/CoverSplash";
import FloatingDecorations from "../Components/HomeDecorations/FloatingDecorations";
import MenuSectionDecorations from "../Components/HomeDecorations/MenuSectionDecorations";
import { getSplashArt, homeLayout, showFloatingIcons } from "../utilis/brandTheme";

// Home page layout, chosen per client in the env file (REACT_APP_HOME_LAYOUT)
const isClassic = homeLayout === "classic";

// Resets on every full page load
let splashShownThisLoad = false;

function Home({ cart, setCart, handleClick, restaurantStatus }) {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const loaderRef = useRef(null);
  const [banners, setBanners] = useState([]);
  const [hovered, setHovered] = useState(false);
  const toggleHover = () => setHovered(!hovered);
  const [showAll, setShowAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingBanners, setLoadingBanners] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [categorySearch, setCategorySearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [isProductSearching, setIsProductSearching] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { defaultImage, logo } = getAppConfig();
  const { restaurantDetails } = useRestaurantDetails();

  const getLocalizedText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object")
      return field[i18n.language] || field.en || "";
    return "";
  };

  const createRipple = useRipple();

  const setCategoryRef = useCategoryAnimation();

  const queryParams = new URLSearchParams(location.search);
  const tableId = queryParams.get("tableId");
  const restaurantId = queryParams.get("restaurantId");
  const isQrEntry = Boolean(tableId && restaurantId);

  //const displayedCategories = showAll ? categories : categories.slice(0, 10);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  useEffect(() => {
    if (!isQrEntry) return;

    const params = new URLSearchParams(location.search);
    const isShareLink = params.has("share");

    const guestInfo = localStorage.getItem("guestInfo");

    if (!guestInfo && !isShareLink) {
      setShowGuestModal(true);
    } else {
      setShowGuestModal(false);
    }
  }, [isQrEntry, location.search]);

  useEffect(() => {
    if (isQrEntry) {
      localStorage.setItem(
        "qrSession",
        JSON.stringify({ restaurantId, tableId }),
      );
    }
  }, [isQrEntry, restaurantId, tableId]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < categories.length) {
          setVisibleCount((prev) => prev + 6); // load 6 more
        }
      },
      { threshold: 1.0 },
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [categories, visibleCount]);

  const displayedCategories = categories.slice(0, visibleCount);
  const hasBanners = loadingBanners || banners.length > 0;

  // Cover splash: on every page load/refresh, while the menu data loads
  // (not again when navigating back to Home inside the app)
  const [splashArt] = useState(getSplashArt);
  const [showSplash, setShowSplash] = useState(() => Boolean(splashArt) && !splashShownThisLoad);
  const hideSplash = useCallback(() => {
    splashShownThisLoad = true;
    setShowSplash(false);
  }, []);

  useEffect(() => {
    const term = categorySearch.trim();
    if (!term) {
      setProductResults([]);
      setIsProductSearching(false);
      return;
    }
    setIsProductSearching(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        params.set("search", term);
        params.set("page", 1);
        params.set("limit", 30);
        if (isQrEntry) {
          params.set("mode", "dinein");
          params.set("tableId", tableId);
          params.set("restaurantId", restaurantId);
        }
        const res = await _get(`/products/menu?${params.toString()}`);
        console.log("[Home Search]", term, "→", res.data.products?.length ?? 0, "results");
        setProductResults(res.data.products || []);
      } catch (err) {
        console.error("[Home Search] API error:", err);
        setProductResults([]);
      } finally {
        setIsProductSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [categorySearch]);

  const fetchBannerData = async () => {
    try {
      setLoadingBanners(true);
      const response = await _get(`/banners`);
      const currentTime = new Date();

      const activeBanners = response.data.filter((b) => {
        const startTime = new Date(b.startDate);
        const endTime = new Date(b.endDate);

        return startTime <= currentTime && endTime > currentTime;
      });

      setBanners(activeBanners.length > 0 ? activeBanners : []);
    } catch (error) {
      console.error("Error fetching banner:", error);
    } finally {
      setLoadingBanners(false);
    }
  };

  useEffect(() => {
    fetchBannerData();
    const interval = setInterval(fetchBannerData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchCategoriesData = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const response = await _get(`/categories`);
      const fetchedCategories = response.data || [];
      setCategories(fetchedCategories);
      setLoadingCategories(false);

      const [offersCountRes, todaysSpecialCountRes, combosCountRes] = await Promise.all([
        (async () => {
          const params = new URLSearchParams();
          params.set("category", "offers");
          params.set("page", "1");
          params.set("limit", "1");
          if (isQrEntry) {
            params.set("mode", "dinein");
            params.set("tableId", tableId);
            params.set("restaurantId", restaurantId);
          }
          try {
            return await _get(`/products/menu?${params.toString()}`);
          } catch (e) {
            console.error("Error fetching offers count:", e);
            return null;
          }
        })(),
        (async () => {
          const params = new URLSearchParams();
          params.set("category", "todays_special");
          params.set("page", "1");
          params.set("limit", "1");
          if (isQrEntry) {
            params.set("mode", "dinein");
            params.set("tableId", tableId);
            params.set("restaurantId", restaurantId);
          }
          try {
            return await _get(`/products/menu?${params.toString()}`);
          } catch (e) {
            console.error("Error fetching todays special count:", e);
            return null;
          }
        })(),
        (async () => {
          const params = new URLSearchParams();
          params.set("category", "combos");
          params.set("page", "1");
          params.set("limit", "1");
          if (isQrEntry) {
            params.set("mode", "dinein");
            params.set("tableId", tableId);
            params.set("restaurantId", restaurantId);
          }
          try {
            return await _get(`/products/menu?${params.toString()}`);
          } catch (e) {
            console.error("Error fetching combos count:", e);
            return null;
          }
        })(),
      ]);
      const offerProductCount = offersCountRes?.data?.totalCount ?? 0;
      const offersCategory =
        offerProductCount > 0
          ? [
              {
                _id: "offers",
                name: t("offers"),
                images: [],
              },
            ]
          : [];
      
      const todaysSpecialCount = todaysSpecialCountRes?.data?.totalCount ?? 0;
      const todaysSpecialCategory =
        todaysSpecialCount > 0
          ? [
              {
                _id: "todays_special",
                name: t("todaysSpecial") || "Today's Special",
                images: [],
              },
            ]
          : [];

      const combosCount = combosCountRes?.data?.totalCount ?? 0;
      const combosCategory =
        combosCount > 0
          ? [
              {
                _id: "combos",
                name: t("combos") || "Combos",
                images: [],
              },
            ]
          : [];

      setCategories([
        ...todaysSpecialCategory,
        ...offersCategory,
        ...combosCategory,
        ...fetchedCategories,
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingCategories(false);
    }
  }, [isQrEntry, tableId, restaurantId, t]);

  useEffect(() => {
    fetchCategoriesData();
  }, [fetchCategoriesData]);

  // Custom arrow components for the slider
  const PrevArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-6 z-10 w-10 h-10 sm:w-14 sm:h-14 bg-white/90 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center hover:bg-brand-500 hover:shadow-2xl transition-all duration-300 border-2 border-brand-200 hover:border-brand-500 group"
    >
      <FaChevronLeft className="text-brand-600 text-base sm:text-xl group-hover:text-white transition-colors" />
    </button>
  );

  const NextArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-6 z-10 w-10 h-10 sm:w-14 sm:h-14 bg-white/90 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center hover:bg-brand-500 hover:shadow-2xl transition-all duration-300 border-2 border-brand-200 hover:border-brand-500 group"
    >
      <FaChevronRight className="text-brand-600 text-base sm:text-xl group-hover:text-white transition-colors" />
    </button>
  );

  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    dotsClass: "slick-dots !relative !bottom-0 !mt-10",
    customPaging: (i) => (
      <div className="w-3 h-3 rounded-full bg-brand-200 hover:bg-brand-500 transition-all duration-300 hover:scale-125"></div>
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
        },
      },
    ],
  };

  const normalizeId = (val) => {
    if (!val) return null;
    if (typeof val === "string") return val;
    if (typeof val === "object" && val.$oid) return val.$oid;
    if (typeof val === "object" && val._id) return val._id;
    return val;
  };

  const handleBannerClick = async (banner) => {
    const productId = normalizeId(banner?.productId);
    if (!productId) return;

    try {
      const res = await _get(`/products/${productId}`);
      const product = res?.data;
      const categoryId = normalizeId(
        product?.category?._id || product?.category,
      );
      const subCatId = normalizeId(
        product?.subcategory?._id || product?.subcategory,
      );
      if (!categoryId) return;

      const params = new URLSearchParams(location.search);
      params.set("category", categoryId);
      if (subCatId) params.set("subcat", subCatId);

      navigate({
        pathname: "/menu",
        search: `?${params.toString()}`,
      });
    } catch (error) {
      console.error("Error opening banner product:", error);
    }
  };
  const handleGuestSubmit = async ({ name, mobile, count }) => {
    try {
      const payload = {
        restaurantId,
        tableId,
        name: name.trim(),
        phone: mobile.trim(),
        customerCount: Number(count),
      };

      const res = await _post("/table/request", payload);
      const allocation = res?.data?.allocation;

      if (!allocation?._id) {
        throw new Error("Allocation ID not received");
      }

      localStorage.setItem("allocationId", allocation._id);
      localStorage.setItem("tableSession", res.data.sessionToken);

      localStorage.setItem(
        "guestInfo",
        JSON.stringify({
          ...payload,
          source: "qr",
        }),
      );

      setShowGuestModal(false);

      enqueueSnackbar("Request sent to restaurant", {
        variant: "success",
      });

      return null; // ✅ success, no error
    } catch (err) {
      const message =
        err?.response?.data?.message || "Unable to submit request. Try again.";

      return message; // ✅ return error to modal
    }
  };
  const handleJoinWithToken = async (token) => {
    try {
      // The code only works at the table whose QR was scanned
      const res = await _post("/allocation/joinroom", { token, tableId });

      const { sessionToken, ...sessionInfo } = res.data;
      localStorage.setItem("allocationId", sessionInfo.allocationId);
      localStorage.setItem("tableSession", sessionToken);
      localStorage.setItem("guestInfo", JSON.stringify(sessionInfo));

      enqueueSnackbar("Joined group order successfully", {
        variant: "success",
      });

      setShowGuestModal(false);
      return null; // ✅ success
    } catch (err) {
      return err?.response?.data?.message || "Invalid or expired token";
    }
  };

  if (restaurantDetails && restaurantDetails.amcStatus && restaurantDetails.amcStatus !== "active") {
    return <ServiceUnavailable />;
  }

  // Featured items (banner carousel)
  const featuredSection = hasBanners && (
    <section
      className={`w-full py-12 sm:py-16 ${isClassic ? "bg-gradient-to-b from-white to-brand-50" : ""}`}
    >
      <div className="w-full px-2 sm:px-8 md:px-12 lg:px-16 xl:px-20">
        <div className="text-center mb-8 sm:mb-10">
          <button
            onClick={(e) => {
              createRipple(e);
              if (isClassic) {
                document
                  .getElementById("category-section")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              } else {
                navigate({ pathname: "/menu", search: location.search });
              }
            }}
            className={`ripple-container inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold mb-6 transition-all duration-300 cursor-pointer tap-scale text-brand-700 shadow-sm hover:shadow-md ${isClassic ? "bg-gradient-to-r from-brand-100 to-brand-50 hover:from-brand-200 hover:to-brand-100" : "bg-brand-100 hover:bg-brand-200"}`}
          >
            <span className="text-lg">🍽️</span> {t("viewFullMenu")}
          </button>
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl mb-4 font-heading font-extrabold text-brand-900 ${isClassic ? "xl:text-6xl tracking-tight" : ""}`}
          >
            {t("featuredItems")}
          </h2>
          <p
            className={`text-base sm:text-lg lg:text-xl max-w-2xl mx-auto font-medium text-brand-600`}
          >
            {t("featuredItemsDesc")}
          </p>
        </div>
        <div className="relative px-0 sm:px-12">
          {loadingBanners ? (
            <div className="grid grid-cols-1 gap-4">
              {[1, 2, 3].map((i) => (
                <BannerSkeleton key={i} />
              ))}
            </div>
          ) : (
            <Slider {...settings}>
              {[...Array(Math.max(3, banners.length))].map((_, index) => {
                const banner = banners[index % banners.length];
                const isClickable = Boolean(
                  normalizeId(banner?.productId),
                );
                return (
                  <div key={index} className="px-1 py-2 sm:px-3">
                    <div
                      className={`bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] border-2 border-brand-100 hover:border-brand-200 group ${isClickable ? "cursor-pointer" : ""
                        }`}
                      onClick={() => {
                        if (!isClickable) return;
                        handleBannerClick(banner);
                      }}
                    >
                      <div className="relative w-full overflow-hidden rounded-xl h-40 sm:h-[220px]">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                        <img
                          src={
                            banner.imagePath
                              ? `${process.env.REACT_APP_IMAGE_BASE_URL}${banner.imagePath.startsWith("/") ? banner.imagePath : `/${banner.imagePath}`
                              }`
                              : defaultImage
                                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                                : "/img/finglider_logo.jpg"
                          }
                          alt={`Banner ${index + 1}`}
                          loading={index === 0 ? "eager" : "lazy"}
                          decoding="async"
                          fetchPriority={index === 0 ? "high" : "auto"}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultImage
                              ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                              : "/img/finglider_logo.jpg";
                          }}
                          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        />
                        {/* Preview button — appears on hover */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImg(
                              `${process.env.REACT_APP_IMAGE_BASE_URL}${banner.imagePath.startsWith("/") ? banner.imagePath : `/${banner.imagePath}`}`,
                            );
                          }}
                          className="absolute bottom-2 end-2 z-20 bg-white/80 hover:bg-white text-gray-700 rounded-full p-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md"
                          title="View full image"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                            />
                          </svg>
                        </button>
                      </div>
                      {banner.description && (
                        <div className="p-6 bg-white border-t border-brand-100">
                          <p className="text-brand-900 text-base font-semibold leading-relaxed">
                            {banner.description}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </Slider>
          )}
        </div>
      </div>
    </section>
  );

  return (
    <>
      <GuestDetailsModal
        open={showGuestModal}
        onSubmit={handleGuestSubmit}
        onJoinWithToken={handleJoinWithToken}
      />

      {showSplash && (
        <CoverSplash
          art={splashArt}
          name={getLocalizedText(restaurantDetails?.name) || getAppConfig().name}
          ready={!loadingCategories && !loadingBanners}
          onHidden={hideSplash}
        />
      )}

      <div
        className={`w-full relative overflow-hidden ${isClassic ? "bg-gradient-to-b from-brand-50 to-page" : "bg-page"}`}
      >
        {/* Floating Food Decorations */}
        {showFloatingIcons && <FloatingDecorations />}

        {/* Offline Status Banner */}
        {restaurantStatus === "offline" && (
          <div className="w-full bg-red-50 border-y border-red-200">
            <div className="w-full px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20 py-4">
              <p className="text-red-700 text-center text-sm font-semibold">
                ⚠️ Currently we are not accepting any online orders
              </p>
            </div>
          </div>
        )}

        {isClassic ? (
          featuredSection
        ) : (
          <ChefPicks
            dineInParams={isQrEntry ? { mode: "dinein", tableId, restaurantId } : null}
            cart={cart}
            setCart={setCart}
            handleClick={handleClick}
          />
        )}

        {/* Browse By Category Section */}
        <section
          id="category-section"
          className={`w-full relative overflow-hidden ${isClassic ? "bg-gradient-to-b from-page to-brand-50 py-16 sm:py-24 min-h-screen" : "pt-10 pb-6 sm:pt-16"}`}
        >
          {/* Additional floating decorations for menu section */}
          {showFloatingIcons && <MenuSectionDecorations />}

          <div className="w-full px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20 relative z-10">
            <div className="text-center mb-12 sm:mb-16">
              <button
                type="button"
                onClick={() => navigate({ pathname: "/menu", search: location.search })}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold mb-6 transition-all duration-300 cursor-pointer bg-brand-100 text-brand-700 shadow-sm hover:shadow-md hover:bg-brand-200`}
              >
                <span className="text-lg">🍴</span>
                {t("ourMenu")}
              </button>
              <h2
                className={`text-3xl sm:text-4xl lg:text-5xl mb-4 font-heading font-extrabold text-brand-900 ${isClassic ? "xl:text-6xl tracking-tight" : ""}`}
              >
                {t("exploreOurMenu")}
              </h2>
              <p
                className={`text-base sm:text-lg lg:text-xl max-w-2xl mx-auto font-medium mb-4 text-brand-600`}
              >
                {t("exploreOurMenuDesc")}
              </p>
              <div
                className="w-24 h-0.5 mx-auto bg-gradient-to-r from-transparent via-brand-400 to-transparent"
              ></div>
            </div>

            {/* Category Search Bar */}
            <div className="relative max-w-lg mx-auto mb-10">
              <div className="relative flex items-center">
                <svg
                  className="absolute start-4 w-5 h-5 text-brand-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  placeholder={t("searchPlaceholder")}
                  className="w-full ps-12 pe-10 py-3.5 bg-white border-2 border-brand-100 focus:border-brand-400 rounded-2xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none shadow-sm transition-colors duration-200"
                />
                {categorySearch && (
                  <button
                    type="button"
                    onClick={() => setCategorySearch("")}
                    className="absolute end-3 text-gray-400 hover:text-brand-500 transition-colors"
                    aria-label="Clear search"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {categorySearch.trim() ? (
              <>
                {isProductSearching ? (
                  <div className="flex justify-center py-16">
                    <div className="w-10 h-10 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin"></div>
                  </div>
                ) : productResults.length > 0 ? (
                  <>
                    <p className="text-sm text-brand-600 font-medium mb-4">
                      {productResults.length} product{productResults.length !== 1 ? "s" : ""} found for &ldquo;{categorySearch}&rdquo;
                    </p>
                    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
                      {productResults.map((product) => (
                        <MenuItem
                          key={product._id}
                          MenuItem={product}
                          handleClick={handleClick}
                          products={productResults}
                          cart={cart}
                          setCart={setCart}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-8 h-8 text-brand-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-brand-900 font-semibold text-base mb-1">
                      No products found
                    </p>
                    <p className="text-brand-400 text-sm">
                      Try a different search term
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-5 lg:gap-6">
                  {loadingCategories
                    ? [...Array(6)].map((_, index) => (
                        <CategorySkeleton key={`skeleton-${index}`} />
                      ))
                    : displayedCategories.map((item, index) => (
                        <div
                          key={item._id}
                          ref={setCategoryRef(index)}
                          className="group cursor-pointer scroll-animate hover-lift"
                          style={{ "--delay": `${index * 0.05}s` }}
                          onClick={(e) => {
                            createRipple(e);
                            const params = new URLSearchParams(location.search);
                            params.set("category", item._id);
                            navigate({
                              pathname: "/menu",
                              search: `?${params.toString()}`,
                            });
                          }}
                        >
                          <div
                            className={`ripple-container overflow-hidden h-full flex flex-col transition-all border ${isClassic ? "bg-gray-50 rounded-2xl sm:rounded-3xl shadow-md hover:shadow-2xl duration-500 border-brand-100 hover:border-brand-300" : "bg-white rounded-2xl shadow-sm hover:shadow-lg duration-300 border-brand-100/80 hover:border-brand-300"}`}
                          >
                            <div
                              className={`relative aspect-square overflow-hidden ${isClassic ? "bg-gradient-to-br from-brand-50 to-page" : "bg-brand-50"}`}
                            >
                              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                              <img
                                src={
                                  item.images?.[0]
                                    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${item.images[0]}`
                                    : defaultImage
                                      ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                                      : "/img/finglider_logo.jpg"
                                }
                                alt={getLocalizedText(item.name)}
                                loading="lazy"
                                decoding="async"
                                className={`w-full h-full object-cover transition-transform ${isClassic ? "duration-700 group-hover:scale-110" : "duration-500 group-hover:scale-105"}`}
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  img.onerror = null;
                                  img.src = defaultImage
                                    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                                    : "/img/finglider_logo.jpg";
                                  img.style.objectFit = "contain";
                                  img.style.backgroundColor = "#fff";
                                }}
                              />
                            </div>
                            <div
                              className={`p-3 sm:p-4 flex items-center justify-center min-h-[52px] sm:min-h-[60px] ${isClassic ? "bg-gradient-to-b from-gray-50 to-brand-50/20" : "bg-white"}`}
                            >
                              <h3 className="text-brand-900 text-xs sm:text-sm font-bold text-center leading-tight line-clamp-2">
                                {getLocalizedText(item.name)}
                              </h3>
                            </div>
                          </div>
                        </div>
                      ))}
                </div>

                <div
                  ref={loaderRef}
                  className="h-20 flex items-center justify-center"
                >
                  {!loadingCategories && visibleCount < categories.length && (
                    <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin"></div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        {!isClassic && featuredSection}

      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 sm:bottom-6 left-4 sm:left-6 z-[70] w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 text-white rounded-full shadow-2xl hover:shadow-brand-500/50 transform hover:scale-110 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center group ring-2 ring-brand-200 ring-offset-2 hover:animate-pulse-glow"
          aria-label="Scroll to top"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6 transform group-hover:-translate-y-0.5 group-hover:scale-110 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {/* Banner full-image lightbox */}
      {previewImg && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm"
          onClick={() => setPreviewImg(null)}
        >
          <div
            className="relative max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewImg(null)}
              className="absolute -top-10 end-0 text-white/80 hover:text-white transition-colors text-3xl leading-none"
              aria-label="Close preview"
            >
              ✕
            </button>
            <img
              src={previewImg}
              alt="Banner preview"
              className="w-full h-auto max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default Home;
